// ============================================
// 属性绑定配置面板
// 为选中的场景节点配置数据绑定
// ============================================

import { useState } from 'react';
import {
  List, Button, Select, Input, Space, Typography, Tag, Popconfirm, Empty, Slider,
} from 'antd';
import { PlusOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { useDataBindingStore } from '../../stores/dataBindingStore';
import { BINDABLE_PROPERTIES, DATA_TRANSFORMS } from '../../types/databinding';
import { generateId } from '@dtwin/shared';
import type { DataBinding } from '../../types/designer';

const { Text } = Typography;

interface BindingPanelProps {
  selectedNodeId: string | null;
}

export default function BindingPanel({ selectedNodeId }: BindingPanelProps) {
  const {
    datasources,
    bindings,
    addBinding,
    removeBinding,
    updateBinding,
    liveData,
    lastEvaluations,
  } = useDataBindingStore();

  const [editingBinding, setEditingBinding] = useState<string | null>(null);

  const nodeBindings = selectedNodeId ? (bindings[selectedNodeId] || []) : [];

  // 获取实时值
  const getLiveValue = (binding: DataBinding) => {
    const data = liveData[binding.datasource];
    if (!data) return undefined;
    if (binding.expression) {
      try {
        const match = binding.expression.match(/^data\.(\w+(?:\.\w+)*)$/);
        if (match) {
          const parts = match[1].split('.');
          let val = data;
          for (const p of parts) val = val?.[p];
          return val;
        }
      } catch {}
    }
    return data;
  };

  const handleAddBinding = () => {
    if (!selectedNodeId) return;
    const newBinding: DataBinding = {
      id: generateId(),
      property: 'config.color',
      datasource: datasources[0]?.id || '',
      expression: '',
      transform: undefined,
    };
    addBinding(selectedNodeId, newBinding);
    setEditingBinding(newBinding.id);
  };

  const handlePropertyChange = (bindingId: string, property: string) => {
    if (selectedNodeId) {
      updateBinding(selectedNodeId, bindingId, { property });
    }
  };

  const handleDatasourceChange = (bindingId: string, datasource: string) => {
    if (selectedNodeId) {
      updateBinding(selectedNodeId, bindingId, { datasource });
    }
  };

  const handleExpressionChange = (bindingId: string, expression: string) => {
    if (selectedNodeId) {
      updateBinding(selectedNodeId, bindingId, { expression });
    }
  };

  const handleTransformChange = (bindingId: string, transform: string | undefined) => {
    if (selectedNodeId) {
      updateBinding(selectedNodeId, bindingId, { transform });
    }
  };

  if (!selectedNodeId) {
    return (
      <div style={{ padding: 12 }}>
        <Empty description="请先选择一个场景节点" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong>属性绑定</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleAddBinding}>
          添加绑定
        </Button>
      </div>

      <List
        dataSource={nodeBindings}
        locale={{ emptyText: '暂无绑定' }}
        renderItem={(binding) => {
          const liveValue = getLiveValue(binding);
          const propMeta = BINDABLE_PROPERTIES.find((p) => p.path === binding.property);

          return (
            <List.Item
              style={{ padding: '8px 0', flexDirection: 'column', alignItems: 'stretch' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <Space size={4}>
                  <LinkOutlined style={{ color: '#1890ff' }} />
                  <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{binding.property}</Text>
                </Space>
                <Popconfirm title="删除绑定？" onConfirm={() => removeBinding(selectedNodeId, binding.id)}>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* 属性选择 */}
                <Select
                  size="small"
                  value={binding.property}
                  onChange={(v) => handlePropertyChange(binding.id, v)}
                  style={{ width: '100%', fontSize: 12 }}
                  placeholder="选择属性"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={BINDABLE_PROPERTIES.map((p) => ({
                    value: p.path,
                    label: `${p.path} (${p.valueType})`,
                  }))}
                />

                {/* 数据源选择 */}
                <Select
                  size="small"
                  value={binding.datasource || undefined}
                  onChange={(v) => handleDatasourceChange(binding.id, v)}
                  style={{ width: '100%', fontSize: 12 }}
                  placeholder="选择数据源"
                  options={datasources.map((ds) => ({
                    value: ds.id,
                    label: `${ds.name} (${ds.type})`,
                  }))}
                />

                {/* 表达式 */}
                <Input
                  size="small"
                  value={binding.expression || ''}
                  onChange={(e) => handleExpressionChange(binding.id, e.target.value)}
                  placeholder="表达式 (如 data.temperature)"
                  style={{ fontSize: 12, fontFamily: 'monospace' }}
                />

                {/* 转换函数 */}
                <Select
                  size="small"
                  value={binding.transform || undefined}
                  onChange={(v) => handleTransformChange(binding.id, v)}
                  style={{ width: '100%', fontSize: 12 }}
                  placeholder="数据转换 (可选)"
                  allowClear
                  options={Object.keys(DATA_TRANSFORMS).map((k) => ({
                    value: k,
                    label: k,
                  }))}
                />

                {/* 实时值预览 */}
                {liveValue !== undefined && (
                  <div style={{ fontSize: 11, color: '#52c41a', fontFamily: 'monospace', background: 'rgba(82,196,26,0.1)', padding: '2px 6px', borderRadius: 4 }}>
                    实时值: {typeof liveValue === 'object' ? JSON.stringify(liveValue) : String(liveValue)}
                  </div>
                )}
              </div>
            </List.Item>
          );
        }}
      />

      {nodeBindings.length > 0 && (
        <div style={{ marginTop: 12, padding: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 4 }}>
          <Text style={{ fontSize: 11, color: '#888' }}>
            共 {nodeBindings.length} 个绑定 | 数据源: {datasources.length} 个
          </Text>
        </div>
      )}
    </div>
  );
}
